import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Editor from '@monaco-editor/react';
import {
  Alert,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  useTheme,
} from '@mui/material';
import _ from 'lodash';

import { Button } from '@components/base';
import { SearchQuery } from '@models/SearchQuery';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { isSearchQuery } from '@modules/layouts/hooks/useSearchAndFilter/utils';
import {
  addFilterIds,
  addIdsToList,
  generateFilter,
  getFilterWithProvider,
  removeFilterIds,
  removeIdsFromList,
} from '@utils/filterUtils';

type EditSearchQueryDialogProps = {
  onClose: VoidFunction;
};

const EditSearchQueryDialog: FC<EditSearchQueryDialogProps> = ({ onClose }) => {
  const { t } = useTranslation();

  const {
    filterCount,
    filterDefLoad: { data: filterDefs },
    provider,
    searchQuery,
    updateSearchQuery,
  } = useSearchAndFilter();

  const getValue = () => {
    let filter = filterCount > 0 ? searchQuery.filter : undefined;

    if (provider && filterDefs)
      filter = filter
        ? getFilterWithProvider(filterDefs, filter, provider)
        : generateFilter(filterDefs, 'ProviderIdentifier', [provider], true);

    const fullQuery = filter
      ? {
          ...searchQuery,
          filter: removeFilterIds(filter),
          orderBy: removeIdsFromList(searchQuery.orderBy),
        }
      : _.omit(searchQuery, 'filter');
    return fullQuery;
  };

  const [error, setError] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [value, setValue] = useState(JSON.stringify(getValue(), null, 2));

  const initValue = useRef(JSON.stringify(getValue(), null, 2));

  const handleConfirm = () => {
    if (JSON.stringify(JSON.parse(value)) === JSON.stringify(JSON.parse(initValue.current))) {
      onClose();
      return;
    }

    try {
      const parsed = JSON.parse(value);
      const isValid = isSearchQuery(parsed);

      if (isValid) {
        const newSearchQuery: SearchQuery = {
          ...parsed,
          ...(parsed.filter ? { filter: addFilterIds(parsed.filter) } : {}),
          orderBy: addIdsToList(parsed.orderBy),
        };

        updateSearchQuery(newSearchQuery);
        onClose();
        return;
      }
    } catch (err) {
      console.error(err);
      setError(true);
    }
  };

  const {
    palette: { mode },
  } = useTheme();

  return (
    <Dialog fullWidth maxWidth='lg' onClose={onClose} open>
      <DialogTitle>{t('searchQuery.edit')}</DialogTitle>

      <Stack component={DialogContent} spacing={2}>
        {error && (
          <Collapse appear>
            <Alert severity='error'>{t('searchQuery.parsingError')}</Alert>
          </Collapse>
        )}

        <Editor
          height='80vh'
          language='json'
          onChange={newValue => setValue(String(newValue))}
          onValidate={markers => setIsValid(markers.length === 0)}
          options={{
            autoClosingBrackets: true,
            formatOnType: true,
            inlineSuggest: true,
            minimap: { scale: 1.5 },
          }}
          theme={mode === 'dark' ? 'vs-dark' : 'light'}
          value={value}
        />
      </Stack>

      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button disabled={!isValid} onClick={handleConfirm} variant='contained'>
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSearchQueryDialog;
